"use client";

import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabaseClient";
import { ListingPreview } from "@/components/listing-preview";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";

const headingLines = [
  "Embracing the Wild",
  "A Visual Storytelling Experience",
  "Through Nature",
];

// Use transform instead of clip-path for better performance (composited animation)
const maskReveal = {
  hidden: { 
    transform: "translateX(-100%)",
    opacity: 0 
  },
  visible: {
    transform: "translateX(0%)",
    opacity: 1,
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
  },
};

/** Las fichas de cada categoría: tres filas de tres en escritorio. */
const FICHAS_POR_CATEGORIA = 9;

// Fetch listings with caching based on filters
// Only fetch listings where website is a URL (not null and not "enabled")
// and that are still available (not reserved, sold, etc.)
const fetchListings = async (agreementType: string, propertyType: string) => {
  const { data, error } = await supabase
    .from('listing_publico')
    .select('*')
    .eq('operation_type_es', agreementType)
    .eq('type_es', propertyType)
    .not('website', 'is', null)
    .neq('website', 'enabled')
    .eq('listing_status', 'available')
    .order('witei_created_at', { ascending: false })
    .limit(FICHAS_POR_CATEGORIA);

  if (error) {
    throw error;
  }

  return data || [];
};

const ListingSkeleton = () => (
  <div className="overflow-hidden border border-gray-200 bg-white">
    <Skeleton className="w-full aspect-[3/2] rounded-none" />
    <div className="flex flex-col gap-2.5 p-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-28" />
        <Skeleton className="h-5 w-12" />
      </div>
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-5 w-3/5" />
      <Skeleton className="mt-1 h-8 w-full" />
    </div>
  </div>
);

const Listings = () => {
  const [agreementType, setAgreementType] = useState<string>("Venta");
  const [propertyType, setPropertyType] = useState<string>("Chalet");
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = React.useRef<HTMLElement>(null);

  // Defer animations until component is visible (improves Speed Index)
  React.useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before visible
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const AGREEMENT_TYPES = [
    { title: "Venta", value: "Venta" },
    { title: "Alquiler", value: "Alquiler" },
  ];

  const PROPERTY_TYPES = [
    { title: "Chalet", value: "Chalet" },
    { title: "Piso", value: "Piso" },
  ];

  const queryClient = useQueryClient();

  // Use React Query for caching and automatic refetching based on selected filters
  // Defer query until component is visible to improve Speed Index
  const { data: listings = [], isLoading } = useQuery({
    queryKey: ['listings', agreementType, propertyType],
    queryFn: () => fetchListings(agreementType, propertyType),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    enabled: isVisible, // Only fetch when component is visible
  });

  // Prefetch other combinations for instant tab switching (only when visible)
  useEffect(() => {
    if (!isVisible) return; // Don't prefetch until component is visible
    
    // Prefetch all other combinations in the background
    const combinations = [
      { agreement: "Venta", property: "Chalet" },
      { agreement: "Venta", property: "Piso" },
      { agreement: "Alquiler", property: "Chalet" },
      { agreement: "Alquiler", property: "Piso" },
    ];

    combinations.forEach(({ agreement, property }) => {
      // Skip the current combination
      if (agreement !== agreementType || property !== propertyType) {
        // Prefetch in the background for instant tab switching
        queryClient.prefetchQuery({
          queryKey: ['listings', agreement, property],
          queryFn: () => fetchListings(agreement, property),
          staleTime: 5 * 60 * 1000,
        });
      }
    });
  }, [agreementType, propertyType, queryClient, isVisible]);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-16">
      <div className="w-full max-w-[75rem] mx-auto space-y-8 sm:space-y-12">
        {/* First part from project-12 (without image) */}
        <div className="flex flex-col gap-6 sm:gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
          <div className="flex-1 lg:max-w-2xl">
            <motion.div
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
            >
              <Button
                variant="secondary"
                className="text-muted-foreground hover:text-foreground mb-3 h-auto p-1 font-normal text-xs sm:text-sm"
              >
                {/*
                 <ArrowLeft className="mr-2 h-4 w-4" />                
                */}
                Person inmobiliaria
              </Button>
            </motion.div>
            <div className="space-y-2 overflow-hidden">
              {/*
              {headingLines.map((line, i) => (
                <motion.h1
                  key={i}
                  className="text-foreground text-3xl font-bold leading-tight md:text-4xl lg:text-5xl"
                  initial="hidden"
                  animate="visible"
                  variants={maskReveal}
                  transition={{
                    delay: i * 0.3,
                    duration: 0.9,
                    ease: "easeInOut",
                  }}
                >
                  {line}
                </motion.h1>
              ))}
              */}
              <motion.h2
                  className="text-foreground text-2xl sm:text-3xl leading-tight md:text-3xl lg:text-3xl"
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                  variants={maskReveal}
                  transition={{
                    delay: 0.3,
                    duration: 0.9,
                    ease: "easeInOut",
                  }}
                >
                  Propiedades en venta y alquiler en Valencia
                </motion.h2>
            </div>
            <motion.p
              className="text-foreground/80 text-sm sm:text-base leading-relaxed mt-3"
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
            >
              Casas, chalets y pisos seleccionados en las mejores zonas de Valencia.
            </motion.p>
          </div>
          <motion.div
            className="mt-1 flex-shrink-0 lg:w-80"
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={fadeUp}
            transition={{ delay: 0.7, duration: 0.8, ease: "easeOut" }}
          >
            <div className="space-y-4 sm:space-y-6">
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                Descubre nuestra cuidada selección de inmuebles en Valencia y
                provincia. Desde viviendas con encanto en el centro hasta chalets
                con piscina en la costa. Te ayudamos a encontrar la propiedad
                ideal o a vender con la mejor estrategia y valoración del mercado.
              </p>
              {/*
              <div className="space-y-4">
                {[
                  ["Year", "2025"],
                  ["Project", "Nature Documentary Web Experience"],
                  ["Photographer", "Lummi Trails"],
                ].map(([label, value], i) => (
                  <motion.div
                    key={label}
                    variants={fadeUp}
                    transition={{
                      delay: 0.9 + i * 0.2,
                      duration: 0.8,
                      ease: "easeOut",
                    }}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                  >
                    <p className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">
                      {label}
                    </p>
                    <p className="text-foreground font-medium">{value}</p>
                  </motion.div>
                ))}
              </div>
              */}
            </div>
          </motion.div>
        </div>

        {/* Tabs and Listings Grid */}
        <div className="border-t pt-6 sm:pt-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
            <ul className="flex h-fit w-fit gap-6 sm:gap-10 tracking-tight">
              {AGREEMENT_TYPES.map((type) => (
                <li
                  key={type.value}
                  className={cn(
                    "cursor-pointer transition-colors text-sm sm:text-base",
                    agreementType === type.value
                      ? "text-foreground"
                      : "text-foreground/30 hover:text-foreground/60",
                  )}
                  onClick={() => setAgreementType(type.value)}
                >
                  {type.title}
                </li>
              ))}
            </ul>
            <ul className="flex h-fit w-fit justify-start sm:justify-end gap-6 sm:gap-10 tracking-tight">
              {PROPERTY_TYPES.map((type) => (
                <li
                  key={type.value}
                  className={cn(
                    "cursor-pointer transition-colors text-sm sm:text-base",
                    propertyType === type.value
                      ? "text-foreground"
                      : "text-foreground/30 hover:text-foreground/60",
                  )}
                  onClick={() => setPropertyType(type.value)}
                >
                  {type.title}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {isLoading ? (
              <>
                {[...Array(FICHAS_POR_CATEGORIA)].map((_, index) => (
                  <ListingSkeleton key={index} />
                ))}
              </>
            ) : (
              <>
                {listings.map((listing) => (
                  <motion.div
                    key={listing.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.3, delay: 0.05 }}
                  >
                    <ListingPreview
                      listing={listing}
                      locale="es"
                    />
                  </motion.div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { Listings };

